<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      attachment: null,
      attachmentPreview: null,
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  computed: {
    isMediaOnly() {
      return !!this.attachment && !this.content;
    },
    isSubmitDisabled() {
      if (this.v$.shortCode.$invalid || this.addCanned.showLoading) return true;
      if (!this.content && !this.attachment) return true;
      return false;
    },
    attachmentIcon() {
      if (!this.attachment) return null;
      const type = this.attachment.type;
      if (type.startsWith('image/')) return 'i-lucide-image';
      if (type.startsWith('video/')) return 'i-lucide-video';
      if (type.startsWith('audio/')) return 'i-lucide-audio-lines';
      return 'i-lucide-file';
    },
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
  },
  methods: {
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.attachment = file;
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        this.attachmentPreview = URL.createObjectURL(file);
      } else {
        this.attachmentPreview = null;
      }
    },
    removeAttachment() {
      this.attachment = null;
      this.attachmentPreview = null;
      this.$refs.fileInput.value = '';
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachment = null;
      this.attachmentPreview = null;
      this.v$.shortCode.$reset();
    },
    addCannedResponse() {
      this.addCanned.showLoading = true;
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          attachment: this.attachment || undefined,
        })
        .then(() => {
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
            <span class="text-n-slate-9 text-sm ml-1">({{ $t('CANNED_MGMT.OPTIONAL') }})</span>
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- Media attachment section -->
        <div class="w-full mt-3">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('CANNED_MGMT.ADD.FORM.MEDIA.LABEL') }}
            <span class="text-n-slate-9 text-sm ml-1">({{ $t('CANNED_MGMT.OPTIONAL') }})</span>
          </label>

          <div
            v-if="attachment"
            class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-1 border border-n-weak mb-2"
          >
            <!-- Image preview -->
            <img
              v-if="attachmentPreview && attachment.type.startsWith('image/')"
              :src="attachmentPreview"
              class="h-16 w-16 object-cover rounded"
              alt="preview"
            />
            <!-- Video preview -->
            <video
              v-else-if="
                attachmentPreview && attachment.type.startsWith('video/')
              "
              :src="attachmentPreview"
              class="h-16 w-16 object-cover rounded"
            />
            <!-- Generic icon -->
            <div
              v-else
              class="h-16 w-16 flex items-center justify-center rounded bg-n-alpha-2"
            >
              <span class="size-8 text-n-slate-10" :class="[attachmentIcon]" />
            </div>

            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ attachment.name }}
              </p>
              <p class="text-xs text-n-slate-9">{{ attachment.type }}</p>
            </div>
            <button
              type="button"
              class="p-1 rounded text-n-slate-9 hover:text-ruby-9"
              @click="removeAttachment"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <div v-else>
            <input
              ref="fileInput"
              type="file"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
              class="hidden"
              @change="onFileChange"
            />
            <button
              type="button"
              class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed border-n-weak text-n-slate-10 hover:border-n-brand hover:text-n-brand transition-colors"
              @click="$refs.fileInput.click()"
            >
              <span class="i-lucide-paperclip size-4" />
              {{ $t('CANNED_MGMT.ADD.FORM.MEDIA.BUTTON') }}
            </button>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2 mt-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="isSubmitDisabled"
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[8rem];

    p {
      @apply text-base;
    }
  }
}
</style>
